import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  output,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ConversationHeaderComponent, ConversationListSearchBarComponent } from '../../organisms';
import { Conversation } from '@clientwire/api';
import { FindByPipe } from '../../../pipes';
import { debounceTime, distinctUntilChanged, fromEvent, map, Subject, Subscription, take, } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AutoSizeVirtualScrollStrategy,
  ListRange,
  RxVirtualFor,
  RxVirtualScrollViewportComponent,
} from '@rx-angular/template/experimental/virtual-scrolling';
import { MessageComponent, Severity } from '@akostm/orbit-ui';
import { ActiveConversationStore, ConversationStore, ConversationTypeStore, ErrorStore, } from '../../../stores';
import { ConversationHelperService, WindowService, WireApiService } from '../../../services';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { faRefresh } from '@fortawesome/free-solid-svg-icons';
import { ConversationFilters } from '../../../interfaces';

@Component({
  selector: 'shared-conversation-list',
  standalone: true,
  imports: [
    ConversationHeaderComponent,
    ConversationListSearchBarComponent,
    FindByPipe,
    RxVirtualFor,
    RxVirtualScrollViewportComponent,
    AutoSizeVirtualScrollStrategy,
    MessageComponent,
    TranslatePipe,
  ],
  templateUrl: './conversation-list.component.html',
  styleUrl: './conversation-list.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationListComponent implements AfterViewInit, OnDestroy {
  conversationList = viewChild('conversationList', { read: ElementRef });
  #viewRendered = false;
  #limitToHaveScrollbar = 10;

  readonly conversationsInited = output<void>();
  readonly conversations = computed(() => this.#conversationStore.entities());
  readonly conversationTypes = computed(() => this.#conversationTypeStore.types());
  readonly activeConversationIds = computed(() => this.#activeConversationStore.ids());

  readonly currentFilters = computed<ConversationFilters>(() => ({
    search: this.#conversationStore.filterString(),
    showArchived: this.#conversationStore.showArchiveOnly(),
    conversationTypes: this.#conversationStore.conversationTypeIds(),
  }));

  readonly hiddenConversationCount = computed(() => this.#conversationStore.hiddenConversationCount());
  readonly SeverityEnum = Severity;
  readonly refreshIcon = faRefresh;
  readonly viewsRendered$ = new Subject<Conversation[]>();
  readonly #minimumConversationSize = 58;
  readonly #conversationStore = inject(ConversationStore);
  readonly #activeConversationStore = inject(ActiveConversationStore);
  readonly #conversationTypeStore = inject(ConversationTypeStore);
  readonly #windowService = inject(WindowService);
  readonly #conversationHelperService = inject(ConversationHelperService);
  readonly #translateService = inject(TranslateService);
  readonly #errorStore = inject(ErrorStore);
  readonly #wireApiService = inject(WireApiService);
  // readonly #destroyRef = inject(DestroyRef);
  readonly #errorLocations = ['getConversations'];
  readonly conversationErrors = computed(() =>
    this.#errorStore.getErrorsByLocations(this.#errorLocations)
  );

  #newConversationsUnsubscribe: (() => void) | null = null;
  #conversationReadStatusUnsubscribe: (() => void) | null = null;
  #conversationArchivedUnsubscribe: (() => void) | null = null;
  #conversationTypeSubscriptions: Subscription[] = [];

  constructor() {
    this.viewsRendered$.pipe(take(1)).subscribe(() => {
      this.#viewRendered = true;
      this.conversationsInited.emit();
    });

    fromEvent(window, 'resize')
      .pipe(
        debounceTime(300),
        map(() => window.innerHeight),
        distinctUntilChanged(),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.#calculateLimitSize();
      });
  }

  ngAfterViewInit(): void {
    this.#calculateLimitSize();
    this.#loadConversationTypesIfNeeded();
  }

  ngOnDestroy(): void {
    this.#unsubscribeFromNewConversations();
    this.#unsubscribeFromConversationReadStatus();
    this.#unsubscribeFromConversationArchived();
    this.#unsubscribeFromConversationTypeChanges();
    this.#conversationHelperService.unsubscribeFromConversationsUpdated();
    this.#conversationHelperService.unsubscribeFromParticipantNotifications();
  }

  #calculateLimitSize(): void {
    // Checking how big is the container and trying to calculate the limit with the most smallest chat messsage
    const conversationWrapperHeight = this.conversationList()?.nativeElement.offsetHeight;

    this.#limitToHaveScrollbar =
      Math.ceil(conversationWrapperHeight / this.#minimumConversationSize) + 2;
    this.#setConversationLimit(this.#limitToHaveScrollbar);

    this.#initConversations();
  }

  isActive(id: string) {
    return this.activeConversationIds()?.includes(id);
  }

  trackByFn(index: number, item: Conversation) {
    return item.id;
  }

  rangeChanged(event: ListRange) {
    const conversations = this.conversations();
    if (this.#viewRendered && conversations && event.end >= conversations.length - 1) {
      this.#conversationShortListScrolled();
    }
  }

  refreshConversations() {
    this.#initConversations();
  }

  clearFilters() {
    // Reset store filters (this will trigger the computed to sync UI)
    this.#conversationStore.resetFilters();

    // Refresh conversation list with cleared filters
    this.refreshConversations();
  }

  changeActiveState(id: string) {
    const isActive = this.#activeConversationStore.entityMap()[id];
    if (isActive) {
      this.#activeConversationStore.removeConversation(id);
      this.#conversationHelperService.unsubscribeFromConversation(id);
    } else {
      const conversation = this.#conversationStore.entityMap()[id];
      if (!conversation)
        throw new Error(
          this.#translateService.instant('SHARED.PAGES.DASHBOARD.NO_ACTIVE_CONVERSATION')
        );
      conversation.conversationData = {
        ...conversation.conversationData,
        count: 0,
      };
      this.#conversationHelperService.addToActiveConversations([conversation]);
    }
  }

  onFiltersChanged(filters: ConversationFilters) {
    // Update the store with the new filters
    this.#conversationStore.changeFilters(filters);

    // Reload conversations with the new filters
    this.#conversationStore.getConversations(this.#windowService.conversationLimit(), 0);
  }

  async #initConversations() {
    // Unsubscribe from events
    this.#unsubscribeFromNewConversations();
    this.#unsubscribeFromConversationReadStatus();
    this.#unsubscribeFromConversationArchived();
    this.#conversationHelperService.unsubscribeFromConversationsUpdated();
    this.#conversationHelperService.unsubscribeFromParticipantNotifications();
    // Get conversations
    await this.#conversationStore.getConversations(this.#windowService.conversationLimit());
    // Subscribe to events
    this.#subscribeToNewConversations();
    this.#subscribeToConversationReadStatus();
    this.#subscribeToConversationArchived();
    this.#conversationHelperService.subscribeToConversationsUpdated();
    this.#conversationHelperService.subscribeToParticipantNotifications();
  }

  #loadConversationTypesIfNeeded() {
    if (this.conversationTypes().length === 0) {
      this.#conversationTypeStore.getConversationTypes();
    }
  }

  #setConversationLimit(limit: number) {
    this.#windowService.setConversationLimit(limit);
  }

  #conversationShortListScrolled() {
    if (
      !this.#conversationStore.loadingConversations() &&
      this.#conversationStore.ids().length < this.#conversationStore.totalElements()
    ) {
      this.#conversationStore.getConversations(10, this.#conversationStore.entities().length);
    }
  }

  #subscribeToNewConversations() {
    this.#newConversationsUnsubscribe = this.#wireApiService.subscribeToNewConversation(
      (payload) => {
        this.#conversationHelperService.addToConversationListByIds([payload.conversationId]);
      }
    );
  }

  #subscribeToConversationArchived() {
    this.#conversationArchivedUnsubscribe = this.#wireApiService.subscribeToConversationArchived(
      (payload) => {
        this.#conversationHelperService.updateConversationArchived(
          payload.conversationId,
          payload.archived
        );
      }
    );
  }

  #unsubscribeFromNewConversations() {
    if (this.#newConversationsUnsubscribe) {
      this.#newConversationsUnsubscribe();
    }
  }

  #subscribeToConversationReadStatus() {
    this.#conversationReadStatusUnsubscribe =
      this.#wireApiService.subscribeToConversationReadStatus((payload) => {
        this.#conversationHelperService.updateConversationReadStatus(payload);
      });
  }

  #unsubscribeFromConversationReadStatus() {
    if (this.#conversationReadStatusUnsubscribe) {
      this.#conversationReadStatusUnsubscribe();
    }
  }

  #unsubscribeFromConversationArchived() {
    if (this.#conversationArchivedUnsubscribe) {
      this.#conversationArchivedUnsubscribe();
    }
  }

  #unsubscribeFromConversationTypeChanges() {
    this.#conversationTypeSubscriptions.forEach((subscription) => subscription.unsubscribe());
    this.#conversationTypeSubscriptions = [];
  }

  onRefreshKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.refreshConversations();
    }
  }

  onChangeActiveStateKeyDown(event: KeyboardEvent, id: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.changeActiveState(id);
    }
  }

  onClearFiltersKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.clearFilters();
    }
  }
}
